import { collectionAllowed } from './analyticsPolicy';
import { analyticsEvent } from './siteAnalytics';
// Browser-side telemetry for Ask and Search.
//
// The Worker can only see the question, the answer and its own provider chain.
// Everything that makes retrieval analysable lives here: which leg surfaced each
// chunk, at what rank and score, how the fusion ordered them, how long the
// in-browser embedding took, which sources the model ended up citing, and which
// results the visitor actually clicked. All of it is posted to the Worker's /t
// endpoint and joined server-side on `event_id`.
//
// Fire-and-forget by design: events are queued, flushed on a microtask (and on
// page hide), sent with sendBeacon where available, and every failure is
// swallowed. Nothing here is allowed to affect what the visitor sees.

import { primaryEndpoint } from './askSite';

const SESSION_KEY = 'ask_telemetry_session';
const SESSION_START_KEY = 'ask_telemetry_started';
const TURN_KEY = 'ask_telemetry_turns';
const FLUSH_DELAY_MS = 400;
const MAX_BATCH = 20;

let queue = [];
let flushTimer = null;
let listenersBound = false;

function enabled() { return collectionAllowed() && !!primaryEndpoint(); }

export function newId() {
  try {
    return crypto.randomUUID();
  } catch {
    return `e_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  }
}

function store(key, value) {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Private mode; the session id just won't survive a reload.
  }
}

function read(key) {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

// One id per browser tab session, so multi-question sessions can be replayed.
export function sessionId() {
  let id = read(SESSION_KEY);
  if (!id) {
    id = newId();
    store(SESSION_KEY, id);
    store(SESSION_START_KEY, String(Date.now()));
  }
  return id;
}

export function sessionAgeMs() {
  const started = Number(read(SESSION_START_KEY));
  return Number.isFinite(started) && started > 0 ? Date.now() - started : null;
}

// 1-based question counter within the session.
export function nextTurnIndex() {
  const next = (Number(read(TURN_KEY)) || 0) + 1;
  store(TURN_KEY, String(next));
  return next;
}

// Everything about the visitor's device the browser will tell us. Geo, IP and
// user-agent are taken server-side instead, where they can't be spoofed.
export function clientSnapshot() {
  if (typeof window === 'undefined') return {};
  const nav = window.navigator || {};
  const screen = window.screen || {};
  const connection = nav.connection || {};
  const safe = (fn, fallback = null) => {
    try {
      return fn();
    } catch {
      return fallback;
    }
  };
  return {
    tz: safe(() => Intl.DateTimeFormat().resolvedOptions().timeZone),
    locale: safe(() => Intl.DateTimeFormat().resolvedOptions().locale),
    lang: nav.language || null,
    langs: Array.isArray(nav.languages) ? nav.languages.slice(0, 6) : null,
    ua_platform: nav.userAgentData?.platform || nav.platform || null,
    mobile: nav.userAgentData?.mobile ?? null,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    screen: `${screen.width || 0}x${screen.height || 0}`,
    dpr: window.devicePixelRatio || null,
    device_memory: nav.deviceMemory || null,
    cores: nav.hardwareConcurrency || null,
    touch: 'ontouchstart' in window || (nav.maxTouchPoints || 0) > 0,
    prefers_dark: safe(() => window.matchMedia('(prefers-color-scheme: dark)').matches),
    reduced_motion: safe(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches),
    connection: connection.effectiveType || null,
    downlink: connection.downlink || null,
    save_data: connection.saveData ?? null,
    session_age_ms: sessionAgeMs(),
    path: safe(() => window.location.pathname + window.location.hash),
    referrer: safe(() => document.referrer || null),
    visibility: safe(() => document.visibilityState),
  };
}

function post(events, useBeacon) {
  const base = primaryEndpoint();
  if (!base || !events.length) return;
  const url = `${base}/t`;
  // text/plain keeps this a CORS-simple request, so no preflight round trip.
  const body = JSON.stringify(events);
  try {
    if (useBeacon && navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
      if (navigator.sendBeacon(url, blob)) return;
    }
    fetch(url, {
      method: 'POST',
      body,
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      keepalive: true,
      mode: 'cors',
    }).catch(() => {});
  } catch {
    // Telemetry must never surface an error to the visitor.
  }
}

function flush(useBeacon = false) {
  window.clearTimeout(flushTimer);
  flushTimer = null;
  if (!queue.length) return;
  const batch = queue.slice(0, MAX_BATCH);
  queue = queue.slice(MAX_BATCH);
  post(batch, useBeacon);
  if (queue.length) flush(useBeacon);
}

function bindFlushListeners() {
  if (listenersBound || typeof window === 'undefined') return;
  listenersBound = true;
  const onHide = () => flush(true);
  window.addEventListener('pagehide', onHide);
  window.addEventListener('beforeunload', onHide);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush(true);
  });
}

// Queue one event. `type` decides which table it lands in server-side.
export function track(event) {
  if (!enabled() || !event || !event.type) return;
  bindFlushListeners();
  queue.push({
    ts: Date.now(),
    session_id: sessionId(),
    page_path: typeof window !== 'undefined' ? (window.location.hash.slice(1).split('?')[0] || window.location.pathname) : null,
    ...event,
  });
  if (queue.length >= MAX_BATCH) {
    flush();
    return;
  }
  if (!flushTimer) flushTimer = window.setTimeout(() => flush(), FLUSH_DELAY_MS);
}

// The identity a question carries through both sides of the pipeline.
export function newAskContext(surface) {
  return {
    event_id: newId(),
    session_id: sessionId(),
    turn_index: nextTurnIndex(),
    surface,
    started_at: Date.now(),
  };
}

export function logAskClient(context, payload) {
  analyticsEvent('site_ask', { surface: context.surface, outcome: payload.error ? 'error' : payload.aborted ? 'cancelled' : 'complete' });
  track({
    type: 'ask_client',
    event_id: context.event_id,
    turn_index: context.turn_index,
    surface: context.surface,
    client: clientSnapshot(),
    ...payload,
  });
  flush();
}

export function logSearch(payload) {
  analyticsEvent('site_search');
  track({ type: 'search', client: clientSnapshot(), ...payload });
}

export function logSearchClick(payload) {
  track({ type: 'search_click', ...payload });
  flush();
}

export function logEvent(name, payload, eventId) {
  if (name === 'view_build_success') analyticsEvent('view_composed', { surface: payload?.surface, outcome: 'complete' });
  if (name === 'view_build_error') analyticsEvent('view_composed', { surface: payload?.surface, outcome: 'error' });
  track({ type: 'client_event', name, event_id: eventId || null, payload: payload || null });
}
